import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { useVendorAuth, vendorFetch } from "./VendorAuthContext";
import { API_BASE } from "../DataContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}-${m}-${y}` : iso;
};

export default function VendorPODetail() {
  const { id } = useParams();
  const { token } = useVendorAuth();
  const [po, setPO] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    vendorFetch(token)(`/api/vendor/purchase-orders/${id}`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error("Not found")))
      .then(setPO)
      .catch((e) => setError(e.message));
  }, [id, token]);

  const downloadPdf = async () => {
    const r = await fetch(`${API_BASE}/api/vendor/purchase-orders/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return alert("PDF download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = po.po_number.replace(/\//g, "_") + ".pdf";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (error) return <div className="text-red-600 text-sm">{error}</div>;
  if (!po) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="vendor-po-detail">
      <Link to="/vendor/pos" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0a2a5e]"><ArrowLeft className="h-4 w-4" /> Back to Purchase Orders</Link>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
        <div className="flex flex-wrap justify-between gap-4 items-start">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Purchase Order</div>
            <div className="font-mono text-lg font-bold text-[#0a2a5e]">{po.po_number}</div>
            <div className="text-sm text-slate-500 mt-1">PO Date: {fmtDate(po.po_date)}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{po.status}</span>
            <Button onClick={downloadPdf} data-testid="vendor-po-pdf-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
              <Download className="h-4 w-4 mr-1" /> Download PDF
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mt-6 text-sm">
          <Stat label="PO Value" value={`₹ ${inr(po.summary?.grand_total)}`} />
          <Stat label="Received Value" value={`₹ ${inr(po.summary?.received_value)}`} />
          <Stat label="Balance Value" value={`₹ ${inr(po.summary?.balance_value)}`} accent />
          <Stat label="Ordered Qty" value={inr(po.summary?.ordered_qty)} />
        </div>

        {(po.reference_no || po.payment_terms || po.delivery_terms || po.expected_delivery) && (
          <div className="grid md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-100 text-sm">
            {po.reference_no && <Kv k="Reference No" v={po.reference_no} />}
            {po.reference_date && <Kv k="Reference Date" v={fmtDate(po.reference_date)} />}
            {po.payment_terms && <Kv k="Payment Terms" v={po.payment_terms} />}
            {po.delivery_terms && <Kv k="Delivery Terms" v={po.delivery_terms} />}
            {po.expected_delivery && <Kv k="Expected Delivery" v={fmtDate(po.expected_delivery)} />}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800">Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Item</th>
                <th className="px-4 py-3 font-semibold text-right">Ordered</th>
                <th className="px-4 py-3 font-semibold text-right">Received</th>
                <th className="px-4 py-3 font-semibold text-right">Balance</th>
                <th className="px-4 py-3 font-semibold text-right">Rate</th>
                <th className="px-4 py-3 font-semibold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {po.items.map((it, idx) => {
                const gross = (Number(it.qty) || 0) * (Number(it.rate) || 0);
                const disc = gross * (Number(it.discount_pct) || 0) / 100;
                const taxable = gross - disc;
                const ordered = Number(it.qty) || 0;
                const received = Number((po.received_by_index || {})[String(idx)] || 0);
                const balance = Math.max(ordered - received, 0);
                const unit = it.unit || "";
                return (
                  <tr key={idx} className="border-t border-slate-100" data-testid={`vendor-po-item-row-${idx}`}>
                    <td className="px-4 py-3 text-slate-400">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{it.item}</div>
                      {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                      {it.hsn && <div className="text-xs text-slate-400 font-mono mt-0.5">HSN: {it.hsn}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">{ordered} {unit}</td>
                    <td className="px-4 py-3 text-right text-slate-700 whitespace-nowrap" data-testid={`vendor-po-item-received-${idx}`}>{received} {unit}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700 whitespace-nowrap" data-testid={`vendor-po-item-balance-${idx}`}>{balance} {unit}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹ {inr(it.rate)}</td>
                    <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">₹ {inr(taxable)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`text-lg font-bold mt-0.5 ${accent ? "text-emerald-700" : "text-slate-800"}`}>{value}</div>
  </div>
);
const Kv = ({ k, v }) => (
  <div>
    <div className="text-xs text-slate-500">{k}</div>
    <div className="text-slate-800 font-medium mt-0.5">{v}</div>
  </div>
);
